import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  DragIndicator,
  Edit,
  Add,
  Delete,
  Save,
  Cancel,
  DragHandle,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import {
  useDroppable,
} from '@dnd-kit/core';
import {
  useSortable,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getImageUrl } from '../../../utils/imageHelper';
import { MusicNote } from '@mui/icons-material';

// Sortable wrapper for SetSection - defined after SetSectionInner

const SortableSongRow = ({ song, onSongClick, getButtonState, hasQuestions, handlePushClick, handleStopQuestion, pushingQuestion, stoppingQuestion, activeFlashSession, loadingFlashSession }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: song.playlist_songs_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const buttonState = getButtonState(song);
  const hasQ = hasQuestions(song.songs_master_id);

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      hover
      onClick={(e) => {
        if (e.target.closest('button') || e.target.closest('.MuiButton-root')) {
          return;
        }
        onSongClick(song);
      }}
      sx={{
        cursor: 'pointer',
        '&:hover': { bgcolor: '#282828' },
        ...(isDragging && { bgcolor: '#333' }),
      }}
    >
      <TableCell sx={{ width: 40, p: 1 }}>
        <IconButton
          {...attributes}
          {...listeners}
          size="small"
          sx={{ color: '#666', cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
        >
          <DragIndicator />
        </IconButton>
      </TableCell>
      <TableCell sx={{ color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {song.image ? (
            <img
              src={getImageUrl(song.image)}
              alt={song.name}
              style={{ width: 50, height: 50, borderRadius: 4, objectFit: 'cover' }}
            />
          ) : (
            <Box sx={{ width: 50, height: 50, bgcolor: '#333', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <MusicNote sx={{ color: '#666' }} />
            </Box>
          )}
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {song.name || 'Unknown Song'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#999', mt: 0.5 }}>
              {song.artist || 'Unknown Artist'}
            </Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
        {loadingFlashSession ? (
          <Button variant="outlined" size="small" disabled sx={{ borderColor: '#666', color: '#666' }}>
            Loading...
          </Button>
        ) : activeFlashSession ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
            {buttonState.type === 'stop' && buttonState.timer && (
              <Typography variant="body2" sx={{ color: buttonState.phase === 'delay' ? '#4caf50' : '#f44336', fontWeight: 'bold', minWidth: '60px' }}>
                {buttonState.timer}
              </Typography>
            )}
            {hasQ ? (
              <Button
                variant={buttonState.type === 'push' ? 'contained' : 'outlined'}
                size="small"
                disabled={buttonState.disabled || (buttonState.type === 'push' && pushingQuestion === song.songs_master_id)}
                onClick={() => {
                  if (buttonState.type === 'push') {
                    handlePushClick(song);
                  } else {
                    handleStopQuestion();
                  }
                }}
                sx={{
                  ...(buttonState.type === 'push' ? {
                    bgcolor: '#9C27B0',
                    '&:hover': { bgcolor: '#7B1FA2' },
                  } : {
                    borderColor: buttonState.color === 'success' ? '#4caf50' : '#f44336',
                    color: buttonState.color === 'success' ? '#4caf50' : '#f44336',
                  }),
                }}
              >
                {buttonState.type === 'push' ? 'Push' : 'Stop'}
              </Button>
            ) : (
              <Button variant="outlined" size="small" disabled sx={{ borderColor: '#666', color: '#666' }}>
                No Questions
              </Button>
            )}
          </Box>
        ) : (
          <Button variant="outlined" size="small" disabled sx={{ borderColor: '#f44336', color: '#f44336' }}>
            Session Not Active
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
};

const SetSectionInner = ({
  set,
  songs,
  onAddSong,
  onEditSetName,
  onDeleteSet,
  onSongClick,
  getButtonState,
  hasQuestions,
  handlePushClick,
  handleStopQuestion,
  pushingQuestion,
  stoppingQuestion,
  activeFlashSession,
  loadingFlashSession,
  setDragAttributes,
  setDragListeners,
  isDragging,
  isCollapsed,
  onToggleCollapse,
}) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(set.set_name);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Calculate total duration for all songs in this set
  const getTotalDuration = (songs = []) => {
    let totalSeconds = 0;

    songs.forEach((song) => {
      if (!song?.duration) return;
      const parts = song.duration.split(":").map(Number);
      if (parts.length === 2) {
        const [min, sec] = parts;
        totalSeconds += (isNaN(min) ? 0 : min) * 60 + (isNaN(sec) ? 0 : sec);
      } else if (parts.length === 3) {
        const [hr, min, sec] = parts;
        totalSeconds += (isNaN(hr) ? 0 : hr) * 3600 + (isNaN(min) ? 0 : min) * 60 + (isNaN(sec) ? 0 : sec);
      }
    });

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours} hr ${minutes} min ${seconds} sec`;
    } else {
      return `${minutes} min ${seconds} sec`;
    }
  };

  const totalDuration = getTotalDuration(songs);

  // Make this set a droppable zone
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `set-${set.set_id}`,
  });

  // Auto-collapse when dragging
  React.useEffect(() => {
    if (isDragging && !isCollapsed && onToggleCollapse) {
      onToggleCollapse();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging]);

  const handleSaveName = () => {
    if (editedName.trim() && editedName !== set.set_name) {
      onEditSetName(set.set_id, editedName.trim());
    }
    setIsEditingName(false);
  };

  const handleCancelEdit = () => {
    setEditedName(set.set_name);
    setIsEditingName(false);
  };

  const handleDelete = () => {
    if (songs.length === 0) {
      onDeleteSet(set.set_id);
      setDeleteDialogOpen(false);
    } else {
      setDeleteDialogOpen(true);
    }
  };

  const handleConfirmDelete = () => {
    onDeleteSet(set.set_id);
    setDeleteDialogOpen(false);
  };

  return (
    <Box ref={setDroppableRef}>
      <Paper 
        sx={{ 
          bgcolor: '#1e1e1e', 
          p: 2,
          border: isOver ? '2px dashed #4caf50' : isDragging ? '2px solid #4caf50' : '2px solid transparent',
          transition: 'all 0.2s ease',
          boxShadow: isDragging ? '0 4px 12px rgba(76, 175, 80, 0.3)' : 'none',
          cursor: setDragAttributes && setDragListeners ? 'grab' : 'default',
          '&:active': { cursor: 'grabbing' },
        }}
      >
        {/* Set Header - Draggable Area */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            mb: isCollapsed ? 0 : 2,
            p: 1.5,
            borderRadius: 1,
            bgcolor: isOver ? 'rgba(76, 175, 80, 0.1)' : isDragging ? 'rgba(76, 175, 80, 0.15)' : 'transparent',
            transition: 'all 0.2s ease',
            ...(isDragging && { bgcolor: '#333' }),
            cursor: setDragAttributes && setDragListeners ? 'grab' : 'default',
            '&:active': setDragAttributes && setDragListeners ? { cursor: 'grabbing' } : {},
            position: 'relative',
            userSelect: 'none',
          }}
        >
          <Box 
            sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}
            {...(setDragAttributes || {})}
            {...(setDragListeners || {})}
          >
            {setDragAttributes && setDragListeners && (
              <Box
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  color: '#666', 
                  cursor: 'grab', 
                  '&:active': { cursor: 'grabbing' },
                  '&:hover': { color: '#4caf50' },
                  pointerEvents: 'none',
                }}
              >
                <DragHandle />
              </Box>
            )}
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (onToggleCollapse) onToggleCollapse();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              onPointerDown={(e) => {
                e.stopPropagation();
              }}
              size="small"
              sx={{ 
                color: '#999',
                '&:hover': { color: '#4caf50', bgcolor: 'rgba(76, 175, 80, 0.1)' },
                zIndex: 10,
                position: 'relative',
                pointerEvents: 'auto',
              }}
            >
              {isCollapsed ? <ExpandMore /> : <ExpandLess />}
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              {isEditingName ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                <TextField
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  size="small"
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: '#666' },
                    },
                  }}
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') handleSaveName();
                    if (e.key === 'Escape') handleCancelEdit();
                  }}
                />
                <IconButton onClick={handleSaveName} size="small" sx={{ color: '#4caf50' }}>
                  <Save />
                </IconButton>
                <IconButton onClick={handleCancelEdit} size="small" sx={{ color: '#f44336' }}>
                  <Cancel />
                </IconButton>
              </Box>
            ) : (
              <>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                  {set.set_name}
                </Typography>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setIsEditingName(true);
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                  size="small"
                  sx={{ color: '#999', zIndex: 10, position: 'relative' }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </>
            )}
            <Typography variant="body2" sx={{ color: '#999' }}>
              ({songs.length} {songs.length === 1 ? 'song' : 'songs'})
            </Typography>
            {songs.length > 0 && (
              <Typography variant="body2" sx={{ color: '#666', ml: 1 }}>
                • {totalDuration}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Add />}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onAddSong(set.set_id);
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              sx={{
                borderColor: '#4caf50',
                color: '#4caf50',
                '&:hover': { borderColor: '#45a049', bgcolor: 'rgba(76, 175, 80, 0.1)' },
                zIndex: 10,
                position: 'relative',
              }}
            >
              Add Song
            </Button>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleDelete();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              size="small"
              sx={{ color: '#f44336', zIndex: 10, position: 'relative' }}
            >
              <Delete />
            </IconButton>
          </Box>
          </Box>
        </Box>

        {/* Songs Table */}
        {!isCollapsed && (
          <>
            {songs.length === 0 ? (
              <Box 
                sx={{ 
                  textAlign: 'center', 
                  py: 4, 
                  color: isOver ? '#4caf50' : '#999',
                  border: isOver ? '2px dashed #4caf50' : 'none',
                  borderRadius: 1,
                  transition: 'all 0.2s ease',
                  bgcolor: isOver ? 'rgba(76, 175, 80, 0.05)' : 'transparent',
                }}
              >
                <Typography>
                  {isOver ? 'Drop song here' : 'No songs in this set'}
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#2c2c2c' }}>
                      <TableCell sx={{ width: 40, color: 'white', fontWeight: 'bold' }}></TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Song Name</TableCell>
                      <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Push</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <SortableContext
                      items={songs.map((song) => song.playlist_songs_id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {songs.map((song) => (
                        <SortableSongRow
                          key={song.playlist_songs_id}
                          song={song}
                          onSongClick={onSongClick}
                          getButtonState={getButtonState}
                          hasQuestions={hasQuestions}
                          handlePushClick={handlePushClick}
                          handleStopQuestion={handleStopQuestion}
                          pushingQuestion={pushingQuestion}
                          stoppingQuestion={stoppingQuestion}
                          activeFlashSession={activeFlashSession}
                          loadingFlashSession={loadingFlashSession}
                        />
                      ))}
                    </SortableContext>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: '#1e1e1e',
            color: 'white',
          },
        }}
      >
        <DialogTitle>Delete Set</DialogTitle>
        <DialogContent>
          <Typography>
            This set contains {songs.length} song(s). Are you sure you want to delete it?
            All songs will be removed from the playlist.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: 'white' }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{ bgcolor: '#f44336', '&:hover': { bgcolor: '#d32f2f' } }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Sortable wrapper for SetSection
const SortableSetSection = ({ set, ...props }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: set.set_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <Box ref={setNodeRef} style={style} sx={{ mb: 2 }}>
      <SetSectionInner 
        set={set} 
        setDragAttributes={attributes} 
        setDragListeners={listeners}
        isDragging={isDragging}
        {...props} 
      />
    </Box>
  );
};

const SetSection = (props) => {
  // If this is being used in a sortable context, use the sortable wrapper
  // Otherwise, use the inner component directly
  return <SetSectionInner {...props} />;
};

export default SetSection;
export { SortableSetSection };

